import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { BoxService } from '@app/services/boxes/box.service';
import { FulfillService } from '../../fulfill.service';
import { SwitchOrderPackageComponent } from '@pages/workspace/fulfill/components/fulfill-side-panel-order-items/components/switch-order-package/switch-order-package.component';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@storybook/services/toast/toast.service';
import { PanelState } from '@pages/workspace/fulfill/utils/models';
import { getDefaultCurrencySign, getSignOfCurrency } from 'src/utils';
import { CurrencyUnit } from '@pdqdev/pdq-shared/dist/entities';
import { ShopService } from '../../../../../services/shops/shop.service';
import { Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { TranslatedFulfillmentStatus } from '@pdqdev/pdq-shared/dist/OrderData';

const PLATFORMS_THAT_DONT_SUPPORT_PARTIAL_FULFILLMENT = ['shipstation', '3pl-central'];
const PLATFORMS_THAT_HAVE_ONLY_INTERNAL_FULFILLMENT = ['woocommerce'];
@Component({
  selector: 'fulfill-side-panel-order-item-card',
  templateUrl: './fulfill-side-panel-order-item-card.component.html',
  styleUrls: ['./fulfill-side-panel-order-item-card.component.scss'],
  standalone: false,
})
export class FulfillSidePanelOrderItemCardComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('counter') counterRef;
  @ViewChild('pickingCounter') pickingCounterRef;
  @ViewChild('tracingNumberInput') tracingNumberInputRef;
  @Input() orderItem;
  @Input() order;
  @Input() isDigital = false;
  @Input() itemFulfilled = false;
  @Input() itemPicked = false;
  @Input() itemPartiallyPicked = false;
  @Input() disableActions: boolean = false;
  @Input() includeAccessory: boolean = false;
  @Input() includeMultiItemsOrder: boolean = false;
  @Input() isPickAndPackFocus: boolean = false;
  @Output() onChange = new EventEmitter();
  statusIcon;
  statusIconColor;
  fulfillmentClass;
  itemWeight;
  weightFromPlatform;
  counter;
  pickingCounter;
  box;
  isMultiParcel;
  isSupportsMeasurments;
  isSupportTracingNumber;
  isDisableManualFulfillment: boolean = false;
  activeBatch;
  hasBox;
  hasBundle = false;
  destroy$ = new Subject();
  currencySign;
  perItemFulfillNotSupportedByPlatform;
  onlyInternalFulfillment;
  showTracingNumber = false;
  tracingNumbers = [];
  tracingNumbersError = '';
  isTracingNumberLoading = false;
  tracingNumberUnsavedChanges = false;
  tracingNumberDuplicates = 0;
  isPickingMode: boolean = false;
  pickmentTagValue;
  showProperties = true;
  id;

  constructor(
    private fulfillService: FulfillService,
    private boxService: BoxService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private shopService: ShopService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.activeBatch = this.fulfillService.activeBatch;
    this.initShopMetadataSettings();
    this.itemWeight = this.getItemWeight();
    this.counter = this.orderItem.quantity - this.orderItem.fulfillableQuantity - (this.orderItem.holdQuantity ?? 0);
    this.isPickingMode = this.fulfillService.isPickingMode;
    if (this.isPickingMode) {
      if (this.itemFulfilled) {
        this.pickmentTagValue = `${this.orderItem.quantity}/${this.orderItem.quantity} items picked`;
      } else {
        this.pickingCounter = this.orderItem.quantity - (this.orderItem.pickableQuantity ?? this.orderItem.quantity);
        this.pickmentTagValue = `${this.pickingCounter}/${this.orderItem.quantity} items picked`;
      }
    }
    if (this.onlyInternalFulfillment && this.itemFulfilled) {
      this.orderItem.fulfillableQuantity = 0;
      this.orderItem.fulfillmentStatus = TranslatedFulfillmentStatus.FULFILLED;
    }
    this.hasBundle =
      this?.order?.orderItems?.some((item) => item.bundled) || this?.order?.digitalItems?.some((item) => item.bundled);
    this.initTracingNumber();
    this.initBoxInfo();
    this.initOrderCardClasses();

    this.fulfillService.onUpdateOrderItem.pipe(takeUntil(this.destroy$)).subscribe((partialOrderItem: any) => {
      if (partialOrderItem.id == this.orderItem.id) {
        this.orderItem = { ...this.orderItem, ...partialOrderItem };
        this.initTracingNumber();
      }
    });

    this.isDisableManualFulfillment = this.fulfillService.isDisableManualFulfillmentAndPickNPackRole();
  }

  private initShopMetadataSettings() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      if (!shop) {
        return;
      }

      this.perItemFulfillNotSupportedByPlatform =
        shop.platform && PLATFORMS_THAT_DONT_SUPPORT_PARTIAL_FULFILLMENT.includes(shop.platform);
      this.onlyInternalFulfillment =
        shop.platform && PLATFORMS_THAT_HAVE_ONLY_INTERNAL_FULFILLMENT.includes(shop.platform);
      const { metadata } = shop;
      this.isMultiParcel = metadata?.isMultiParcelSupport;
      this.isSupportsMeasurments = metadata?.supportsMeasuresValidation;
      this.isSupportTracingNumber = metadata?.supportsTracingNumber;
      this.currencySign = getDefaultCurrencySign();
      if (metadata.currency) {
        this.currencySign = getSignOfCurrency(metadata?.currency.toUpperCase?.() as CurrencyUnit);
      }
    });
  }

  private initTracingNumber() {
    this.tracingNumbers = this.orderItem.tracingNumbers?.map((tracing) => tracing.tracingNumber) ?? [];
    this.tracingNumberUnsavedChanges = false;
    this.checkForTracingNumberDuplicates();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.initOrderCardClasses();
    }
  }

  initBoxInfo() {
    const box = this.orderItem?.box || this.orderItem?.variant?.box;
    if (box) {
      const boxName = this.boxService.utils.getBoxName(box);
      this.box = `${boxName} ${box.distanceUnit}`;
      this.hasBox = !!boxName && !!box.distanceUnit;
    }
  }

  initOrderCardClasses() {
    this.isPickingMode ? this.setPickmentClass() : this.setFulfillmentClass();
  }

  getItemWeight() {
    let variant;
    let weight;
    let unit;

    try {
      if (Object.keys(this.orderItem).includes('variantEntry')) {
        variant = this.orderItem.variantEntry;
      } else {
        variant = this.orderItem.variant;
      }

      if (variant.weight) {
        weight = variant.weight;
        this.weightFromPlatform = true;
      } else {
        weight = variant.weightAmount;
      }

      unit = variant.weightUnit;

      return weight + '' + unit;
    } catch (e) {
      console.warn('no variant');
      return '';
    }
  }

  setFulfillmentClass(newQuantity?) {
    if (this.itemFulfilled || newQuantity) {
      if (this.itemFulfilled || this.orderItem.quantity === newQuantity) {
        this.statusIcon = 'filled_v1';
        this.statusIconColor = '#25CEA6';
        this.fulfillmentClass = 'fulfilled';
      } else {
        this.fulfillmentClass = 'partial';
        this.statusIcon = '';
      }
    } else {
      this.fulfillmentClass = 'none';
      this.statusIcon = '';
    }
  }

  setPickmentClass(newQuantity?) {
    if (this.itemFulfilled || this.itemPicked || this.itemPartiallyPicked || newQuantity) {
      if (this.itemFulfilled || this.itemPicked || this.orderItem.quantity === newQuantity) {
        this.statusIcon = this.itemFulfilled ? 'filled_v1' : '';
        this.statusIconColor = this.itemFulfilled ? '#25CEA6' : '';
        this.fulfillmentClass = 'fulfilled';
      } else if (this.itemPartiallyPicked || newQuantity) {
        this.fulfillmentClass = 'partial';
        this.statusIcon = '';
      }
    } else {
      this.fulfillmentClass = 'none';
      this.statusIcon = '';
    }
  }

  updateFulfillmentQuantity(newQuantity) {
    if (!this.fulfillService.checkAbilityToFulfill()) return;
    this.setFulfillmentClass(newQuantity);
    if (this.orderItem.sourceQuantity) {
      newQuantity += this.orderItem.sourceQuantity - this.orderItem.fulfillableQuantity;
    }
    const itemToFulfill = {
      id: this.orderItem.externalId,
      pdqId: this.orderItem.id,
      fulfillmentStatus: this.orderItem.fulfillmentStatus,
      fulfillmentId: this.orderItem.fulfillmentExternalId,
      quantity: newQuantity,
      quantityFulfilled: this.orderItem.sourceQuantity
        ? this.orderItem.sourceQuantity - this.orderItem.fulfillableQuantity
        : this.orderItem.quantity - this.orderItem.fulfillableQuantity,
      product_external_id: this.orderItem.product.externalIdNumber,
    };

    this.fulfillService.updateItemsToFulfill([itemToFulfill]);
    this.counter = newQuantity;
    this.onChange.emit();
  }

  updatePickmentQuantity(newQuantity) {
    if (!this.fulfillService.checkAbilityToFulfill()) return;
    this.setPickmentClass(newQuantity);
    if (this.orderItem.sourceQuantity) {
      newQuantity += this.orderItem.sourceQuantity - (this.orderItem.pickableQuantity ?? this.orderItem.quantity);
    }
    const itemToPick = {
      pdqId: this.orderItem.id,
      quantity: newQuantity,
      internalStatus: this.orderItem.internalStatus,
      // quantityPickable: this.orderItem.sourceQuantity
      //   ? this.orderItem.sourceQuantity - (this.orderItem.pickableQuantity ?? 0)
      //   : this.orderItem.quantity - (this.orderItem.pickableQuantity ?? 0),
    };

    this.fulfillService.updateItemsToPick([itemToPick]);
    this.pickingCounter = newQuantity;
    this.onChange.emit();
  }

  getItemImage(image) {
    return image ? image : '../../../../../../../assets/images/product-placeholder.png';
  }

  switchPackage(): void {
    const switchDialogRef = this.dialog.open(SwitchOrderPackageComponent, {
      panelClass: 'overflow-visible-class',
      data: { orderItem: this.orderItem, order: this.order },
      disableClose: true,
    });

    switchDialogRef.afterClosed().subscribe((event: { success: boolean; updatedBox: any }) => {
      if (event && event.success) {
        this.box = this.boxService.utils.getBoxName(event?.updatedBox);
        this.hasBox = !!this.box;
        this.toastService.show(PdqToastComponent, { message: 'Package details updated', mode: 'info', duration: 3000 });
      } else if (event && event.success == false) {
        this.fulfillService.setActiveBatch({
          ...this.activeBatch,
          errors: ['Order was detached from batch and moved back to assign'],
        });
        this.fulfillService.setPanelState(PanelState.BATCH);
      }
    });
  }

  onTracingNumberLostFocus(event) {
    this.addTracingNumber(event);
    setTimeout(() => {
      if (!this.isTracingNumberLoading) {
        this.checkForTracingNumberUnsavedChanges();
      }
    }, 300);
  }

  onTracingNumberEnter() {
    this.tracingNumberInputRef.focus();
  }

  onTracingNumberAdd(event) {
    this.addTracingNumber(event);
  }

  private addTracingNumber(event: any) {
    if (event.target.value) {
      this.tracingNumbers.push(event.target.value);
      event.target.value = '';
      this.checkForTracingNumberDuplicates();
    }
  }

  onTracingNumberRemove(tracingNumberIndex) {
    this.tracingNumbers.splice(tracingNumberIndex, 1);
    this.checkForTracingNumberDuplicates();
  }

  saveTracingNumbers() {
    this.isTracingNumberLoading = true;
    this.fulfillService
      .updateTracingNumbers(this.order.id, this.orderItem.id, this.tracingNumbers)
      .pipe(
        catchError((error) => {
          this.isTracingNumberLoading = false;
          this.checkForTracingNumberUnsavedChanges();
          throw error;
        }),
      )
      .subscribe(() => (this.isTracingNumberLoading = false));
  }

  checkForTracingNumberUnsavedChanges() {
    this.tracingNumberUnsavedChanges =
      this.tracingNumbers?.length !== this.orderItem.tracingNumbers?.length ||
      this.tracingNumbers?.some((tracingNumber, i) => tracingNumber != this.orderItem.tracingNumbers[i].tracingNumber);
    if (!this.isTracingNumberLoading) {
      this.updateTracingNumbersError();
    }
  }

  checkForTracingNumberDuplicates() {
    let duplicates = 0;
    this.tracingNumbers?.forEach(
      (tracingNumber, i) => (duplicates += this.tracingNumbers.indexOf(tracingNumber) !== i ? 1 : 0),
    );
    this.tracingNumberDuplicates = duplicates;
    this.updateTracingNumbersError();
  }

  updateTracingNumbersError() {
    const errors = [];
    this.tracingNumberDuplicates &&
      errors.push(this.tracingNumberDuplicates + ' duplicate number' + (this.tracingNumberDuplicates > 1 ? 's' : ''));
    this.tracingNumberUnsavedChanges && errors.push('Unsaved changes');
    this.tracingNumbersError = errors.join(', ');
  }
}
